import { useMemo, useState } from 'react';
import { FiAlertTriangle } from 'react-icons/fi';
import TextEdit from '../../../molecules/TextEdit';
import DateField from '../../../molecules/DateField';
import CurrencyInput from '../../../molecules/CurrencyInput';
import SelectTextField from '../../../molecules/SelectTextField';
import { getErrorMessage } from '../../../../utils/formErrorUtils';
import type { ErrorField } from '../../../../types/ErrorField';
import type { PersonById } from '../../../../services/personApi';
import type {
    Claim,
    ClaimItem,
    CreateManualPaymentPayload,
    ObligationType,
    Party,
    PaymentMovementType,
} from '../../../../types/LegalCase';
import { displayPartyName } from './partyDisplay';

interface LegalCasePaymentFormProps {
    claim?: Claim | null;
    // Partes do caso: alimentam o seletor de pagador. O avalista não é escolhido como "tipo de
    // pagamento" — ele é a parte com papel GUARANTOR, e o backend deriva isso do papel.
    parties?: Party[];
    resolvedPeople?: Record<string, PersonById>;
    errorFields: ErrorField[];
    isSaving: boolean;
    onSubmit: (value: CreateManualPaymentPayload) => Promise<boolean>;
    onCancel: () => void;
}

// A âncora do lançamento: um título específico, ou a operação inteira. Nunca os dois (o backend
// recusa com 422) — daí ser um seletor de modo, e não dois campos que o usuário pode preencher
// juntos.
type AnchorMode = 'TITLE' | 'OPERATION';

interface OperationOption {
    value: string;
    label: string;
    obligationType: ObligationType;
    externalReference: string;
    fromCollection: boolean;
}

// Campos do payload que já têm lugar próprio na tela — o resto vira erro residual (não some).
const OWN_FIELDS = [
    'amount', 'occurredAt', 'movementType', 'claimItemId',
    'obligationType', 'externalReference', 'payerPartyId',
];

// Erros de validação (422) sem campo próprio na tela — mostrados em bloco para não sumirem.
const residualErrors = (errorFields: ErrorField[]): ErrorField[] =>
    errorFields.filter((e) => !OWN_FIELDS.includes(e.fieldName));

// Título da cobrança: o pagamento chega sozinho pelo evento, e lançar à mão contaria duas vezes
// (ADR 005, decisão 7). Mesmo critério do backend, que recusa com 422.
const isFromCollection = (item: ClaimItem): boolean =>
    item.obligationType === 'TITLE_DEBT' || !!item.obligationId;

// Saldo que falta pagar do título — é o que um "pagamento total" quita. O valor cheio do título
// seria errado quando já houve pagamento parcial (registraria pagamento a maior).
const remainingOf = (claim: Claim | null | undefined, claimItemId: string): number => {
    const item = (claim?.items || []).find((i) => i.id === claimItemId);
    return item?.remainingAmount ?? 0;
};

const MOVEMENT_OPTIONS: { value: PaymentMovementType; label: string }[] = [
    { value: 'PARTIAL_PAYMENT', label: 'Pagamento parcial' },
    { value: 'TOTAL_PAYMENT', label: 'Pagamento total' },
];

const ANCHOR_OPTIONS: { value: AnchorMode; label: string }[] = [
    { value: 'TITLE', label: 'Um título específico' },
    { value: 'OPERATION', label: 'A operação inteira (ex.: aval)' },
];

// Operações distintas da pretensão: as chaves `(tipo, referência)` únicas dos títulos não-garantia
// (garantia fica fora do total — ADR 002). Uma operação com qualquer título da cobrança é
// bloqueada pelo backend, então é marcada aqui para sair da lista com explicação.
const operationOptions = (claim?: Claim | null): OperationOption[] => {
    const seen = new Map<string, OperationOption>();
    for (const item of claim?.items || []) {
        if (item.isGuarantee || !item.obligationType || !item.externalReference) continue;
        const value = `${item.obligationType}|${item.externalReference}`;
        const existing = seen.get(value);
        if (existing) {
            // basta UM título da cobrança para a operação inteira ficar bloqueada
            existing.fromCollection = existing.fromCollection || isFromCollection(item);
            continue;
        }
        seen.set(value, {
            value,
            label: `${item.obligationTypeFormatted} ${item.externalReference}`,
            obligationType: item.obligationType,
            externalReference: item.externalReference,
            fromCollection: isFromCollection(item),
        });
    }
    return Array.from(seen.values());
};

const partyOption = (party: Party, resolved?: PersonById) => ({
    value: party.id,
    // O papel no rótulo é o que deixa o avalista escolhível sem um campo "é avalista?".
    label: `${displayPartyName(party, resolved)} • ${party.roleFormatted}`,
});

// Formulário de lançamento manual de pagamento (o VALOR AVAL da planilha, um PIX avulso). Ancora
// num título OU na operação: o aval **nasce para a operação**, não para a parcela, e sem essa
// segunda âncora não havia como registrá-lo pela tela (ADR 005, decisão 2.1). Estado local: o form
// é pequeno e não compartilha regra com outro.
const LegalCasePaymentForm = ({
    claim,
    parties,
    resolvedPeople,
    errorFields,
    isSaving,
    onSubmit,
    onCancel,
}: LegalCasePaymentFormProps) => {
    const [anchorMode, setAnchorMode] = useState<AnchorMode>('TITLE');
    const [amount, setAmount] = useState(0);
    const [occurredAt, setOccurredAt] = useState('');
    const [movementType, setMovementType] = useState<PaymentMovementType>('PARTIAL_PAYMENT');
    const [claimItemId, setClaimItemId] = useState('');
    const [operationKey, setOperationKey] = useState('');
    const [payerPartyId, setPayerPartyId] = useState('');
    const [description, setDescription] = useState('');

    // Entram como âncora os títulos que não são garantia (fora do total — ADR 002) e que não vêm
    // da cobrança (o pagamento desses chega pelo evento).
    const billable = useMemo(() => (claim?.items || []).filter((i) => !i.isGuarantee), [claim]);
    const hasCollectionTitles = billable.some(isFromCollection);
    const titleOptions = useMemo(
        () => billable
            .filter((i) => !isFromCollection(i))
            .map((i) => ({
                value: i.id,
                label: `${i.titleNumber}${i.externalReference ? ` • ${i.externalReference}` : ''}`,
            })),
        [billable],
    );

    const operations = useMemo(() => operationOptions(claim), [claim]);
    const availableOperations = useMemo(
        () => operations.filter((o) => !o.fromCollection),
        [operations],
    );
    const blockedOperations = operations.length - availableOperations.length;

    const payerOptions = useMemo(
        () => (parties || []).map((p) => partyOption(p, p.personId ? resolvedPeople?.[p.personId] : undefined)),
        [parties, resolvedPeople],
    );

    const anchorChosen = anchorMode === 'TITLE' ? !!claimItemId : !!operationKey;
    const canSubmit = amount > 0 && anchorChosen;

    // Pagamento total preenche o valor com o saldo do título. Continua editável: liquidação com
    // desconto paga menos que o saldo e segue sendo "total". Só vale para a âncora de título — a
    // operação não tem "saldo do título" a preencher (o valor do aval é o que o jurídico souber).
    const changeMovementType = (value: PaymentMovementType) => {
        setMovementType(value);
        if (value === 'TOTAL_PAYMENT' && anchorMode === 'TITLE' && claimItemId) {
            setAmount(remainingOf(claim, claimItemId));
        }
    };

    // Trocar de título com "total" selecionado re-preenche com o saldo do título novo.
    const changeClaimItem = (value: string) => {
        setClaimItemId(value);
        if (movementType === 'TOTAL_PAYMENT' && value) setAmount(remainingOf(claim, value));
    };

    // Trocar de modo limpa a âncora do outro modo: enviar os dois é 422 no backend, e o estado
    // local não pode guardar uma escolha invisível na tela.
    const changeAnchorMode = (value: AnchorMode) => {
        setAnchorMode(value);
        setClaimItemId('');
        setOperationKey('');
    };

    const submit = async () => {
        const operation = availableOperations.find((o) => o.value === operationKey);
        await onSubmit({
            amount,
            occurredAt: occurredAt || undefined,
            movementType,
            claimItemId: anchorMode === 'TITLE' ? claimItemId : undefined,
            obligationType: anchorMode === 'OPERATION' ? operation?.obligationType : undefined,
            externalReference: anchorMode === 'OPERATION' ? operation?.externalReference : undefined,
            payerPartyId: payerPartyId || undefined,
            description: description || undefined,
        });
    };

    return (
        <div className="rounded-lg border border-gray-200 dark:border-slate-700 p-3 space-y-3">
            <p className="text-xs text-gray-500 dark:text-gray-400">
                Lançar pagamento. O valor pago é a soma dos lançamentos.
            </p>

            <SelectTextField
                label="A que o pagamento se refere"
                items={ANCHOR_OPTIONS}
                value={anchorMode}
                onChange={(e) => changeAnchorMode(e.target.value as AnchorMode)}
            />

            {anchorMode === 'TITLE' && (
                <>
                    {hasCollectionTitles && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Títulos da cobrança não aparecem aqui: os pagamentos deles chegam
                            automaticamente pela cobrança.
                        </p>
                    )}
                    {titleOptions.length === 0 && !hasCollectionTitles && (
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                            Cadastre um título na pretensão antes de lançar um pagamento.
                        </p>
                    )}
                    <SelectTextField
                        label="Título"
                        items={[{ value: '', label: '—' }, ...titleOptions]}
                        value={claimItemId}
                        onChange={(e) => changeClaimItem(e.target.value)}
                        errorMessage={getErrorMessage(errorFields, 'claimItemId')}
                    />
                </>
            )}

            {anchorMode === 'OPERATION' && (
                <>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                        O valor abate a dívida da operação, sem ser atribuído a um título — é como o
                        aval entra, porque o avalista responde pela operação, não pela parcela.
                    </p>
                    {blockedOperations > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Operações com título da cobrança não aparecem aqui: os pagamentos delas
                            chegam automaticamente pela cobrança.
                        </p>
                    )}
                    {availableOperations.length === 0 && blockedOperations === 0 && (
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                            Cadastre um título com operação na pretensão antes de lançar por operação.
                        </p>
                    )}
                    <SelectTextField
                        label="Operação"
                        items={[{ value: '', label: '—' }, ...availableOperations.map((o) => ({ value: o.value, label: o.label }))]}
                        value={operationKey}
                        onChange={(e) => setOperationKey(e.target.value)}
                        errorMessage={
                            getErrorMessage(errorFields, 'externalReference')
                            || getErrorMessage(errorFields, 'obligationType')
                        }
                    />
                </>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <CurrencyInput
                    label="Valor pago"
                    prefix="R$"
                    fractionDigits={2}
                    value={amount}
                    onChange={setAmount}
                    errorMessage={getErrorMessage(errorFields, 'amount')}
                />
                <DateField
                    label="Data do pagamento (opcional)"
                    value={occurredAt}
                    onChange={setOccurredAt}
                    errorMessage={getErrorMessage(errorFields, 'occurredAt')}
                />
            </div>

            <SelectTextField
                label="Tipo"
                items={MOVEMENT_OPTIONS}
                value={movementType}
                onChange={(e) => changeMovementType(e.target.value as PaymentMovementType)}
                errorMessage={getErrorMessage(errorFields, 'movementType')}
            />

            <div className="space-y-1">
                <SelectTextField
                    label="Quem pagou (opcional)"
                    items={[{ value: '', label: '—' }, ...payerOptions]}
                    value={payerPartyId}
                    onChange={(e) => setPayerPartyId(e.target.value)}
                    errorMessage={getErrorMessage(errorFields, 'payerPartyId')}
                />
                {payerOptions.length === 0 && (
                    <p className="text-[10px] text-gray-500 dark:text-gray-400">
                        Cadastre as partes do caso para poder atribuir o pagamento a quem pagou.
                    </p>
                )}
            </div>

            <TextEdit
                label="Observação (opcional)"
                value={description}
                onChange={(e: any) => setDescription(e.target.value)}
            />

            {residualErrors(errorFields).map((e) => (
                <div key={e.fieldName} className="flex items-center gap-2 text-sm text-red-700 dark:text-red-400">
                    <FiAlertTriangle size={14} />
                    <span>{e.message}</span>
                </div>
            ))}

            <div className="flex items-center justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"
                >
                    Cancelar
                </button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={isSaving || !canSubmit}
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                    {isSaving ? 'Salvando...' : 'Lançar pagamento'}
                </button>
            </div>
        </div>
    );
};

export default LegalCasePaymentForm;
